'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { reduxLogger } from '@/core/utils/logger';
import { useTour } from '../hooks/useTour';
import { useCompleteTourWithTracking } from '../hooks/useCompleteTourWithTracking';
import { useTourAutoAdvance } from '../hooks/useTourAutoAdvance';
import { TourHighlight } from '../components/TourHighlight';
import { AmsConnectWelcomeModal } from './AmsConnectWelcomeModal';
import { AmsConnectCompleteModal } from './AmsConnectCompleteModal';
import { AMS_CONNECT_TOUR_STEPS } from './types';

/** Count of highlight-type steps (for "Step X of Y" display) */
const HIGHLIGHT_STEPS = AMS_CONNECT_TOUR_STEPS.filter((s) => s.type === 'highlight');

/**
 * AmsConnectTourOverlay - Renders the AMS Connect tour step UI.
 *
 * Only renders when the active tour is 'ams-connect'. Navigates the user
 * to Integrations to walk through connecting HawkSoft and
 * enabling contact sync.
 */
export function AmsConnectTourOverlay() {
  const router = useRouter();
  const { isActive, activeTourId, currentStep, nextStep } = useTour();
  const { completeTourWithTracking } = useCompleteTourWithTracking();

  const isAmsTour = activeTourId === 'ams-connect';

  // Auto-advance polling
  useTourAutoAdvance(isAmsTour, currentStep, nextStep);

  // Track which steps we've already navigated for (to avoid re-navigating)
  const navigatedForStepRef = useRef<string | null>(null);

  // Handle step-specific navigation
  useEffect(() => {
    if (!isAmsTour || !currentStep) return;
    if (navigatedForStepRef.current === currentStep.id) return;

    // All highlight steps happen on the integrations page
    const integrationSteps = [
      'highlight_hs_connect',
      'highlight_hs_modal',
      'highlight_contact_sync',
    ];
    if (integrationSteps.includes(currentStep.id)) {
      navigatedForStepRef.current = currentStep.id;
      router.push('/settings?section=integrations');
      reduxLogger.info(`[AmsConnectTour] Navigating to Integrations for step: ${currentStep.id}`);
    }
  }, [isAmsTour, currentStep?.id, currentStep, router]);

  const handleWelcomeComplete = useCallback(() => {
    router.push('/settings?section=integrations');
    reduxLogger.info('[AmsConnectTour] Navigating to Integrations');
    nextStep();
  }, [router, nextStep]);

  const handleFinish = useCallback(() => {
    completeTourWithTracking('ams-connect');
    router.push('/dashboard?view=all');
  }, [completeTourWithTracking, router]);

  const handleHighlightNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  if (!isAmsTour || !isActive || !currentStep) return null;

  // Backdrop visibility — block clicks only for non-interactive, non-auto-advancing highlight steps
  const isHighlightStep = currentStep.type === 'highlight';
  const isClickThrough = !!(
    currentStep.advanceOnNavigate ||
    currentStep.advanceOnElement ||
    currentStep.advanceOnElementRemoved
  );
  const isInteractive = !!currentStep.allowInteraction;
  const showBackdrop = isHighlightStep && !isClickThrough && !isInteractive;

  return (
    <>
      {/* Persistent backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 199,
          pointerEvents: showBackdrop ? 'auto' : 'none',
          opacity: showBackdrop ? 1 : 0,
        }}
      />

      {/* Step content */}
      {currentStep.type === 'modal' && (
        <>
          {currentStep.id === 'ams_welcome' && <AmsConnectWelcomeModal onComplete={handleWelcomeComplete} />}
          {currentStep.id === 'ams_complete' && <AmsConnectCompleteModal onFinish={handleFinish} />}
        </>
      )}

      {currentStep.type === 'highlight' &&
        (() => {
          const highlightIndex = HIGHLIGHT_STEPS.findIndex((s) => s.id === currentStep.id);
          return (
            <TourHighlight
              step={currentStep}
              stepNumber={highlightIndex + 1}
              totalSteps={HIGHLIGHT_STEPS.length}
              onNext={handleHighlightNext}
              isLastStep={highlightIndex === HIGHLIGHT_STEPS.length - 1}
            />
          );
        })()}
    </>
  );
}
